import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { rgba } from 'polished';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ArchiveIcon, CheckmarkIcon, CloseIcon, SendIcon, UndoIcon } from '@treams/treams-icons';
import { Avatar, Badge, ContextMenu, Label, MoreButton, Sidebar, Spinner } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

const Name = styled(Label)<{ selected?: boolean }>`
	color: ${(props) => props.theme.colors.slateBlue};

	${(props) => props.selected && `color: ${props.theme.colors.primary};`}
`;

const Email = styled(Label)`
	color: ${(props) => props.theme.colors.lightBlueDark};
	font-weight: normal;

	${(props) => props.selected && `color: ${rgba(props.theme.colors.primary, 0.5)};`}
`;

export default function FeedbackRecipient(props: {
	request: graphql.FeedbackRequest;
	response: graphql.FeedbackResponse;
}) {
	const { t } = useTranslation();

	const [hoverContainer, setHoverContainer] = useState(false);
	const [hoverMore, setHoverMore] = useState(false);

	const [cancel, { loading: loadingCancel }] = graphql.useArchiveFeedbackResponseMutation({
		variables: { input: { responseID: props.response.id } },
	});

	const [restore, { loading: loadingRestore }] = graphql.useRestoreFeedbackResponseMutation({
		variables: { input: { responseID: props.response.id } },
	});

	const [resend, { loading: loadingResend }] = graphql.useSendFeedbackReminderMutation({
		variables: { input: { responseID: props.response.id } },
	});

	return (
		<Sidebar.Item
			small
			hover={hoverContainer && !hoverMore}
			path={[
				props.request.user.isViewer
					? `/feedback/${props.request.id}`
					: `/users/${props.request.user.id}/feedback/${props.request.id}`,
				`response_id=${props.response.id}`,
			].join('?')}
			onMouseEnter={() => setHoverContainer(true)}
			onMouseLeave={() => setHoverContainer(false)}
		>
			{({ selected }) => (
				<>
					<Badge.Container
						badge={
							<>
								{props.response.status === graphql.FeedbackResponseStatus.Shared && (
									<Badge color="green" icon={<CheckmarkIcon />} />
								)}
								{props.response.status === graphql.FeedbackResponseStatus.Cancelled && (
									<Badge color="red" icon={<CloseIcon />} />
								)}
							</>
						}
						mr={2}
					>
						<Avatar user={props.response.recipient} size={32} flexGrow={0} flexShrink={0} />
					</Badge.Container>
					<Box flexGrow={1} flexShrink={1}>
						<Name selected={selected}>
							{props.response.recipient.firstName} {props.response.recipient.lastName}
						</Name>
						<Email selected={selected} mt={1}>
							{props.response.recipient.email}
						</Email>
					</Box>
					{loadingCancel || loadingResend || loadingRestore ? (
						<Flex width={24} height={24} alignItems="center" justifyContent="center">
							<Spinner />
						</Flex>
					) : (
						<Box flexGrow={0} flexShrink={0}>
							<ContextMenu.Target
								menu={
									<ContextMenu>
										{props.response.status === graphql.FeedbackResponseStatus.Cancelled && (
											<ContextMenu.Item
												onClick={() => restore()}
												icon={<UndoIcon />}
												title={t('feedback.recipient.restore')}
											/>
										)}
										{props.response.status === graphql.FeedbackResponseStatus.Draft && (
											<>
												<ContextMenu.Item
													onClick={() => resend()}
													icon={<SendIcon />}
													title={t('feedback.recipient.resend')}
												/>
												<ContextMenu.Item
													onClick={() => cancel()}
													icon={<CloseIcon />}
													title={t('feedback.recipient.cancel')}
												/>
											</>
										)}
										{props.response.status === graphql.FeedbackResponseStatus.Shared && (
											<ContextMenu.Item
												onClick={() => cancel()}
												icon={<ArchiveIcon />}
												title={t('feedback.recipient.archive')}
											/>
										)}
									</ContextMenu>
								}
							>
								<MoreButton
									onMouseEnter={() => setHoverMore(true)}
									onMouseLeave={() => setHoverMore(false)}
								/>
							</ContextMenu.Target>
						</Box>
					)}
				</>
			)}
		</Sidebar.Item>
	);
}

function FeedbackRecipientSkeleton() {
	return (
		<Sidebar.Custom>
			<Avatar.Skeleton size={32} mr={2} flexGrow={0} flexShrink={0} />
			<Box flexGrow={1} flexShrink={1}>
				<Label.Skeleton />
				<Label.Skeleton mt={1} />
			</Box>
		</Sidebar.Custom>
	);
}

FeedbackRecipient.Skeleton = FeedbackRecipientSkeleton;
